import React, { useState } from 'react';
import { Search, TrendingUp, Sparkles, LineChart, ArrowUpRight, ChevronRight } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppBadge } from '../components/base/AppBadge';
export function CostManagementPage() {
  const [activeTab, setActiveTab] = useState('In evidenza');
  return <div className="min-h-screen bg-[#121212] p-4 md:p-8" data-id="element-2123">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 mb-10" data-id="element-2124">
        <div className="flex-1" data-id="element-2125">
          <h1 className="md:text-[32px] text-white tracking-tight mb-3 text-[28px] font-[500]" data-id="element-2126">
            Ciao, Marco!
            <br data-id="element-2127" />
            Ecco il riepilogo dei costi di oggi.
          </h1>
          <p className="text-[14px] text-white/60 max-w-2xl mb-6 leading-relaxed" data-id="element-2128">
            I costi del servizio Customer Assistant mostrano un picco insolito,
            mentre gli altri servizi sono nella norma. Su 12 servizi monitorati,
            1 richiede attenzione.
          </p>

          {/* Toggle Buttons */}
          <div className="flex items-center gap-3" data-id="element-2129">
            <button className="flex items-center gap-2 px-1.5 py-1.5 pr-4 rounded-full bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors" data-id="element-2130">
              <span className="flex items-center justify-center min-w-[24px] h-[24px] px-2 bg-amber-500/20 rounded-full text-[12px] font-semibold" data-id="element-2131">
                1
              </span>
              <span className="text-[13px] font-medium" data-id="element-2132">Da verificare</span>
            </button>
            <button className="flex items-center gap-2 px-1.5 py-1.5 pr-4 rounded-full bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 transition-colors" data-id="element-2133">
              <span className="flex items-center justify-center min-w-[24px] h-[24px] px-2 bg-indigo-500/20 rounded-full text-[12px] font-semibold" data-id="element-2134">
                11
              </span>
              <span className="text-[13px] font-medium" data-id="element-2135">Nella norma</span>
            </button>
          </div>
        </div>

        {/* Decorative Card */}
        <div className="relative w-full lg:w-[420px] min-h-[160px] rounded-2xl bg-gradient-to-br from-indigo-500/20 via-purple-500/10 to-transparent border border-white/[0.05] overflow-hidden flex items-end p-4 shrink-0" data-id="element-2136">
          {/* Abstract decorative shapes */}
          <div className="absolute top-[-20%] right-[-10%] w-48 h-48 bg-purple-500/20 rounded-full blur-3xl mix-blend-screen" data-id="element-2137"></div>
          <div className="absolute bottom-[-10%] left-[20%] w-40 h-40 bg-indigo-500/20 rounded-full blur-3xl mix-blend-screen" data-id="element-2138"></div>
          <div className="absolute top-[20%] left-[-10%] w-32 h-32 bg-blue-500/20 rounded-full blur-2xl mix-blend-screen" data-id="element-2139"></div>

          <button className="relative z-10 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-white text-[13px] font-medium hover:bg-white/20 transition-colors shadow-lg" data-id="element-2140">
            AI Data Visualisation
            <ArrowUpRight className="w-4 h-4 text-white/70" data-id="element-2141" />
          </button>
        </div>
      </div>

      {/* Tabs & Search Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/[0.08] pb-0 mb-6" data-id="element-2142">
        <div className="flex items-center gap-6 overflow-x-auto custom-scrollbar" data-id="element-2143">
          {['In evidenza', 'Per te', 'Tutti i servizi'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-[14px] font-medium transition-colors whitespace-nowrap relative ${activeTab === tab ? 'text-white' : 'text-white/50 hover:text-white/80'}`} data-id="element-2144">
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white rounded-t-full" data-id="element-2145"></div>}
            </button>)}
        </div>

        <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.03] border border-white/[0.08] rounded-lg mb-3 sm:mb-0 focus-within:border-white/20 focus-within:bg-white/[0.05] transition-colors" data-id="element-2146">
          <Search className="w-4 h-4 text-white/40" data-id="element-2147" />
          <input type="text" placeholder="Cerca servizi..." className="bg-transparent border-none text-[13px] text-white placeholder:text-white/40 focus:outline-none w-full sm:w-48" data-id="element-2148" />
        </div>
      </div>

      {/* Metric Cards Section */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2149">
        {/* Card 1: Customer Assistant (Large) */}
        <div className="lg:col-span-2 bg-gradient-to-br from-white/5 to-transparent border border-white/20 rounded-2xl p-6 flex flex-col relative overflow-hidden" data-id="element-2150">
          <div className="flex items-start justify-between mb-4 relative z-10" data-id="element-2151">
            <h3 className="text-[16px] font-medium text-white/80" data-id="element-2152">
              Customer Assistant
            </h3>
            <AppBadge variant="default" className="bg-white/10 text-white border-none" data-id="element-2153">
              Da verificare
            </AppBadge>
          </div>

          <div className="mb-6 relative z-10" data-id="element-2154">
            <p className="font-semibold text-white text-[24px]" data-id="element-2155">€1,675</p>
          </div>

          {/* Custom Inline SVG Chart */}
          <div className="w-full h-[120px] mt-auto relative z-10 text-white/80" data-id="element-2156">
            <svg viewBox="0 0 400 120" className="w-full h-full overflow-visible" preserveAspectRatio="none" data-id="element-2157">
              {/* Background faded bars */}
              <g opacity="0.1" fill="currentColor" data-id="element-2158">
                {Array.from({
                length: 40
              }).map((_, i) => {
                // Create a subtle wave pattern for the background bars
                const height = 40 + Math.sin(i * 0.3) * 10 + (i > 30 ? (i - 30) * 4 : 0);
                return <rect key={i} x={i * 10} y={120 - height} width="6" height={height} rx="2" data-id="element-2159" />;
              })}
              </g>

              {/* Dashed threshold line */}
              <line x1="300" y1="10" x2="300" y2="120" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" opacity="0.4" data-id="element-2160" />

              {/* Solid line path */}
              <path d="M 0 90 Q 50 100, 100 80 T 200 95 T 300 85 L 390 30" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-2161" />

              {/* Labels */}
              <text x="0" y="75" fill="currentColor" opacity="0.7" fontSize="12" fontWeight="500" data-id="element-2162">
                1.3K
              </text>
              <text x="365" y="20" fill="currentColor" opacity="0.7" fontSize="12" fontWeight="500" data-id="element-2163">
                1.6K
              </text>
            </svg>
          </div>

          {/* Footer Action Bar */}
          <button className="w-full flex items-center justify-between px-4 py-3.5 bg-white/10 text-white/80 rounded-xl mt-6 hover:bg-white/20 transition-colors relative z-10" data-id="element-2164">
            <div className="flex items-center gap-2.5" data-id="element-2165">
              <TrendingUp className="w-4 h-4" data-id="element-2166" />
              <span className="text-[13px] font-medium" data-id="element-2167">
                17% sopra la media mensile
              </span>
            </div>
            <ChevronRight className="w-4 h-4" data-id="element-2168" />
          </button>
        </div>

        {/* Card 2: Smart Matcher (Small) */}
        <div className="lg:col-span-1 bg-[#1a1a1a] border border-white/[0.06] rounded-2xl p-6 flex flex-col" data-id="element-2169">
          <div className="flex items-start justify-between mb-4" data-id="element-2170">
            <h3 className="text-[16px] font-medium text-white/80" data-id="element-2171">
              Smart Matcher
            </h3>
            <AppBadge variant="info" className="bg-indigo-500/20 text-indigo-300 border-none" data-id="element-2172">
              Nella norma
            </AppBadge>
          </div>

          <div className="mb-6" data-id="element-2173">
            <p className="font-semibold text-white text-[24px]" data-id="element-2174">€420</p>
          </div>

          <div className="flex flex-col gap-2 mt-auto pt-8" data-id="element-2175">
            <button className="w-full flex items-center justify-between px-4 py-3 bg-indigo-500/10 text-indigo-300 rounded-xl hover:bg-indigo-500/20 transition-colors" data-id="element-2176">
              <span className="text-[13px] font-medium" data-id="element-2177">AI Summary</span>
              <Sparkles className="w-4 h-4" data-id="element-2178" />
            </button>
            <button className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.04] text-white/80 rounded-xl hover:bg-white/[0.08] transition-colors" data-id="element-2179">
              <span className="text-[13px] font-medium" data-id="element-2180">Data Insights</span>
              <LineChart className="w-4 h-4" data-id="element-2181" />
            </button>
          </div>
        </div>

        {/* Card 3: Content Moderator (Small) */}
        <div className="lg:col-span-1 bg-[#1a1a1a] border border-white/[0.06] rounded-2xl p-6 flex flex-col" data-id="element-2182">
          <div className="flex items-start justify-between mb-4" data-id="element-2183">
            <h3 className="text-[16px] font-medium text-white/80" data-id="element-2184">
              Content Moderator
            </h3>
            <AppBadge variant="info" className="bg-indigo-500/20 text-indigo-300 border-none" data-id="element-2185">
              Nella norma
            </AppBadge>
          </div>

          <div className="mb-6" data-id="element-2186">
            <p className="font-semibold text-white text-[24px]" data-id="element-2187">€340</p>
          </div>

          <div className="flex flex-col gap-2 mt-auto pt-8" data-id="element-2188">
            <button className="w-full flex items-center justify-between px-4 py-3 bg-indigo-500/10 text-indigo-300 rounded-xl hover:bg-indigo-500/20 transition-colors" data-id="element-2189">
              <span className="text-[13px] font-medium" data-id="element-2190">AI Summary</span>
              <Sparkles className="w-4 h-4" data-id="element-2191" />
            </button>
            <button className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.04] text-white/80 rounded-xl hover:bg-white/[0.08] transition-colors" data-id="element-2192">
              <span className="text-[13px] font-medium" data-id="element-2193">Data Insights</span>
              <LineChart className="w-4 h-4" data-id="element-2194" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}